import { useQuery } from '@tanstack/react-query'
import { LogOut, Monitor, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { LoginDialog } from './components/auth/login-dialog'
import { LoginForm } from './components/auth/login-form'
import { KitchenDisplay } from './components/kds/kitchen-display'
import { TestModeBanner } from './components/test-mode-banner'
import { Button } from './components/ui/button'
import { forceUpdate, useAutoUpdate } from './hooks/useAutoUpdate'
import { useScreenWakeLock } from './hooks/useScreenWakeLock'
import { useAuth, useRequestSession } from './lib/api/auth'
import { gqlRequest, graphqlErrorMessage } from './lib/api/client'
import { KitchenDisplayQuery, MyRestaurantsQuery, RestaurantKitchenDisplaysQuery } from './lib/api/operations'
import { useConfiguration } from './lib/useConfiguration'

export function parseRoute(url: URL) {
  const path = url.pathname.split('/').filter(Boolean).map(decodeURIComponent)
  if (path[0] === 'order' || path[0] === 'buffet') return null
  if (path.length === 2) return { kind: 'kitchen' as const, restaurantId: path[0], displayId: path[1] }
  const config = (url.searchParams.get('c') || url.hash.slice(1)).split('/').map(decodeURIComponent)
  if (config.length === 2 && config.every(Boolean)) return { kind: 'kitchen' as const, restaurantId: config[0], displayId: config[1] }
  return null
}

export function App() {
  const { session, ready } = useAuth()
  const route = parseRoute(new URL(window.location.href))
  useScreenWakeLock()
  useAutoUpdate()
  return (
    <div className="flex h-dvh flex-col bg-background text-foreground">
      <TestModeBanner />
      {!ready ? (
        <div className="p-8" role="status">
          Laddar…
        </div>
      ) : !route ? (
        <Picker />
      ) : (
        <>
          <LoginDialog open={!session} />
          <KitchenScreen restaurantId={route.restaurantId} displayId={route.displayId} />
        </>
      )}
    </div>
  )
}

function Picker() {
  const { session, signOut } = useAuth()
  const request = useRequestSession()
  const [restaurantId, setRestaurantId] = useState('')
  const restaurants = useQuery({ queryKey: ['restaurants', request.mode], enabled: Boolean(session), queryFn: () => gqlRequest(request, MyRestaurantsQuery) })
  const displays = useQuery({
    queryKey: ['displays', request.mode, restaurantId],
    enabled: Boolean(session && restaurantId),
    queryFn: () => gqlRequest(request, RestaurantKitchenDisplaysQuery, { restaurantId }),
  })
  return (
    <main className="flex flex-1 items-center justify-center overflow-auto p-6">
      <div className="w-full max-w-lg space-y-6 rounded-2xl border bg-card p-8 shadow-lg">
        <div className="flex items-center gap-3">
          <Monitor className="size-9 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Loco köksskärm</h1>
            <p className="text-sm text-muted-foreground">{session ? 'Välj restaurang och skärm' : 'Logga in med ditt Loco-konto'}</p>
          </div>
        </div>
        {!session ? (
          <LoginForm />
        ) : (
          <>
            {!restaurantId ? (
              <div className="space-y-2">
                {(restaurants.data?.me?.restaurantsWithAccess ?? [])
                  .filter((r) => r.galleyEnabled === true)
                  .map((r) => (
                    <Button key={r.id} className="w-full justify-start" variant="outline" size="lg" onClick={() => setRestaurantId(r.id)}>
                      {r.name}
                    </Button>
                  ))}
                {restaurants.isPending ? (
                  <p>Laddar restauranger…</p>
                ) : !restaurants.data?.me?.restaurantsWithAccess?.some((r) => r.galleyEnabled === true) ? (
                  <p>Inga restauranger med köksskärmen aktiverad. Aktivera den i backoffice.</p>
                ) : null}
              </div>
            ) : (
              <div className="space-y-3">
                {displays.data?.restaurant?.kitchenDisplaysDisabled ? (
                  <p>Köksskärmar är avstängda för restaurangen.</p>
                ) : (
                  (displays.data?.restaurant?.kitchenDisplays ?? []).map((d) => (
                    <Button key={d.id} asChild size="lg" variant="outline" className="w-full justify-start">
                      <a href={`/${restaurantId}/${d.id}`}>{d.name ?? 'Huvudskärm'}</a>
                    </Button>
                  ))
                )}
                <Button variant="ghost" onClick={() => setRestaurantId('')}>
                  Byt restaurang
                </Button>
              </div>
            )}
            {(restaurants.error || displays.error) && (
              <p role="alert" className="text-destructive">
                {graphqlErrorMessage(restaurants.error || displays.error)}
              </p>
            )}
            <Button variant="ghost" onClick={signOut}>
              Logga ut
            </Button>
          </>
        )}
        <p className="text-xs text-muted-foreground">Version {__GALLEY_BUILD_DATE__}</p>
      </div>
    </main>
  )
}

function KitchenScreen({ restaurantId, displayId }: { restaurantId: string; displayId: string }) {
  const { session, signOut } = useAuth()
  const request = useRequestSession()
  const [clock, setClock] = useState(new Date())
  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])
  const query = useQuery({
    queryKey: ['display', request.mode, restaurantId, displayId],
    enabled: Boolean(session),
    refetchInterval: 30_000,
    queryFn: () => gqlRequest(request, KitchenDisplayQuery, { restaurantId, kitchenDisplayId: displayId }),
  })
  const configuration = useConfiguration(request, restaurantId, displayId)
  const restaurant = query.data?.restaurant
  const disabled = restaurant?.galleyEnabled !== true || restaurant?.kitchenDisplaysDisabled === true
  return (
    <>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <a href="/" aria-label="Välj skärm">
            <Monitor className="size-8 text-primary" />
          </a>
          <div>
            <h1 className="font-bold">{configuration.settings.titleOverride || restaurant?.kitchenDisplay?.name || 'Köksskärm'}</h1>
            <p className="text-xs text-muted-foreground">{restaurant?.name ?? restaurantId}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <time className="text-2xl font-bold tabular-nums">{clock.toLocaleTimeString('sv-SE')}</time>
          <Button variant="outline" title="Uppdatera programmet" onClick={forceUpdate}>
            <RefreshCw />
          </Button>
          <Button variant="ghost" onClick={signOut}>
            <LogOut />
            Logga ut
          </Button>
        </div>
      </header>
      {!session ? null : query.isPending ? (
        <p className="p-6">Laddar…</p>
      ) : query.error ? (
        <p role="alert" className="p-6 text-destructive">
          {graphqlErrorMessage(query.error)}
        </p>
      ) : disabled ? (
        <p className="p-6">Köksskärmen är inte aktiverad för restaurangen.</p>
      ) : !restaurant?.kitchenDisplay ? (
        <p className="p-6">Skärmen kunde inte hittas.</p>
      ) : (
        <KitchenDisplay key={`${request.mode}/${restaurantId}/${displayId}`} session={request} restaurantId={restaurantId} kitchenDisplayId={displayId} />
      )}
    </>
  )
}
